import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, batch } from 'react-redux';
import { useLocation, useRouteMatch } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import {
  initializeFlow,
  loadProgress,
  redirectUserToAppropriateFlow,
  placeUserInFlowFromAnswers,
} from '../../actions/flow';
import {
  loginError,
  getUserInfo,
  urlDetails,
  tempUserData,
} from '../../actions/user';
import FlowService from '../../services/flow-service';
import UserSession from '../../services/user-session';
import GaService from '../../services/ga-service';
import {
  UK_VANITY_TO_COUNTRY,
  setUKCountry,
  getSignupRedirect,
  isTrustedDeepLink,
} from '../../services/signup-service';
import { useAppSelector } from '../../hooks/use-app-react-redux';
import Selectors from '../../selectors';
import Loader from '../../components/loader';
import Page from '../page';

const IDP_NAMES = ['test', 'evive', 'rally'];

type MatchParams = {
  vanity: string;
  product: string;
  org: string;
  id: string;
};

const FlowContainer = () => {
  const dispatch = useDispatch();

  const match = useRouteMatch<MatchParams>();
  const location = useLocation();
  const { search } = location;

  // True when route params are present: prevents a flash from a stale Redux store on remount
  // (e.g. initialized=true from a prior WebView session before FLOW_INITIALIZE resets it).
  const [isLoading, setIsLoading] = useState(
    () => !!(match.params.vanity || match.params.product)
  );

  const initialized = useAppSelector(Selectors.selectFlowReducerInitialized);
  const css = useAppSelector(Selectors.selectFlowReducerCurrentFlowCss);

  const verifyUrlDetails = useCallback(() => {
    const urlParams = new URLSearchParams(search);
    const details = urlParams.get('details');
    if (details) {
      dispatch(urlDetails(details));
    }
  }, [search, dispatch]);

  const verifyTempUserData = useCallback(() => {
    if (
      window.temporary_user_data &&
      Object.keys(JSON.parse(window.temporary_user_data)).length
    ) {
      UserSession.setTempUserData(window.temporary_user_data);
    }
    const userData = UserSession.getUserData();
    if (userData?.id) {
      window.temporary_user_data = '{}';
    }

    const temp_user_data = UserSession.getTempUserData();
    if (temp_user_data) {
      dispatch(tempUserData(temp_user_data));
    }
  }, [dispatch]);

  const verifyLoginError = useCallback(() => {
    const urlParams = new URLSearchParams(search);

    if (urlParams.get('login')) {
      // Login failed
      const error = urlParams.get('error');
      Sentry.captureException(
        `Error logging in user: ${error} at location ${location.pathname}`
      );
      dispatch(loginError(error));
      // Record GA login failed event
      GaService.loginFailed(error);
    }
  }, [search, location, dispatch]);

  /**
   * Load progress for user
  It seems to be a precondition of this function that the user has just
  logged in. Note: To be verified.
  */
  const initializeProgress = useCallback(
    async userInfo => {
      if (userInfo && userInfo.is_authenticated) {
        setIsLoading(true);

        const urlParams = new URLSearchParams(search);

        if (
          urlParams.get('login') === 'true' ||
          urlParams.get('login_instead_signup') === 'true'
        ) {
          UserSession.removeTempUserData();
          // Record GA login event
          GaService.login(userInfo);
        } else {
          // User is logged — pre-load local progress while server answers are fetched.
          // placeUserInFlowFromAnswers will dispatch loadProgress() again once answers
          // arrive, which overwrites this with the server-authoritative step.
          dispatch(loadProgress());
        }

        // Cast needed: useDispatch() lacks the thunk overload for Promise return types.
        // batch() prevents a one-frame flash (React 17 doesn't auto-batch Promise callbacks).
        // placeUserInFlowFromAnswers returns true when cross-domain navigation is in progress —
        // keep Loader visible until the new page loads; void (no redirect) → clearLoader() fires.
        const clearLoader = () =>
          batch(() => {
            dispatch(loadProgress());
            setIsLoading(false);
          });
        try {
          const navigating = await (dispatch(
            placeUserInFlowFromAnswers(userInfo.id)
          ) as unknown as Promise<true | void>);
          if (!navigating) {
            clearLoader();
          }
          // else: keep Loader — navigating to onboarding
        } catch {
          clearLoader();
        }
        return;
      }

      // User is not logged in
      const tempData = UserSession.getTempUserData();
      if (
        tempData &&
        Object.keys(tempData).length &&
        IDP_NAMES.indexOf(tempData.idp_name) === -1
      ) {
        dispatch(loadProgress(true)); // this result of this bool was set in redux as redirectAfterSignup, which was never used? should we keep the distinction here?
      } else {
        dispatch(loadProgress());
      }

      setIsLoading(false);
    },
    [search, dispatch]
  );

  const checkOnboardingPlatformAfterLogin = useCallback(
    async userInfo => {
      const urlParams = new URLSearchParams(search);
      if (
        urlParams.get('create_account') === '1' ||
        isTrustedDeepLink(getSignupRedirect())
      ) {
        // In-app browser guard: ?create_account=1 flags the initial entry;
        // isTrustedDeepLink(getSignupRedirect()) covers subsequent pages (post-Auth0, post-email)
        // where the param is gone but the deep link is still in sessionStorage.
        // Without this, a stale Safari cookie yields wrongOnboardingPlatform=true → isLoading forever.
        await initializeProgress(userInfo);
        return;
      }

      // This function is called on every refresh, so we need to be certain
      // that this user did actually log in. If this value we read from the
      // flow service is "null", that indicates this user did not do a login.
      const loginPlatgenFlag = FlowService.getPlatgenFlagFromLogin();

      // If the user did not do a login, they are not on the wrong OnboardingPlatform.
      const wrongOnboardingPlatform =
        loginPlatgenFlag != null &&
        loginPlatgenFlag !== FlowService.getPlatgenFlag();
      if (wrongOnboardingPlatform) {
        try {
          const navigating = await (dispatch(
            redirectUserToAppropriateFlow(userInfo.id)
          ) as unknown as Promise<true | void>);
          if (!navigating) {
            await initializeProgress(userInfo);
          }
        } catch {
          setIsLoading(false);
        }
      } else {
        await initializeProgress(userInfo);
      }
    },
    [search, initializeProgress, dispatch]
  );

  // componentDidMount
  useEffect(() => {
    const { vanity, product, org, id } = match.params;

    // No origin rewrite for the NHS flow: it stays on whichever origin it started on so the
    // Auth0 callback, localStorage, the sl-user-flow cookie and the mobile-handoff
    // sessionStorage redirect_url all stay on one origin. redirect_uri uses the current origin
    // (buildCanonicalRedirectUri), which Auth0 already allows.

    if (org && UK_VANITY_TO_COUNTRY[org]) {
      setUKCountry(UK_VANITY_TO_COUNTRY[org]);
    }

    // TODO (Alex): this should go through dispatch, like verifyLoginError() and verifyUrlDetails() below
    // If it's a redirect from FitBit Login save current path
    const urlParams = new URLSearchParams(search);
    const fitBitData = {
      fitBitCode: urlParams.get('code') || '',
      location: localStorage['current-path'] || '',
    };

    // If user tried to login and login failed, we notify login component
    verifyLoginError();
    verifyUrlDetails();
    verifyTempUserData();

    const getUserInfoCallback = () => {
      dispatch(
        getUserInfo({
          cb: checkOnboardingPlatformAfterLogin,
        })
      );
    };

    // Initialize user progress in flow
    // BTC note: this ia thunk that calls a thunk that calls a thunk. All data in those thunks can come from redux.
    if (vanity || product || (fitBitData.fitBitCode && fitBitData.location)) {
      // TODO (Alex): fitbit data should not be passed here when initializing the flow. Instead, it should go through reducer
      dispatch(
        initializeFlow({
          vanity,
          product,
          org,
          id,
          fitBitData,
          cb: getUserInfoCallback,
        })
      );
      return;
    }

    // not having any of these is an error state, redirect to error page
    console.error('Missing necessary information: ', {
      vanity,
      product,
      fitBitData,
    });
    window.location.href = `${product}/error`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only runs once, componentDidMount replacement, do not add deps

  useEffect(() => {
    if (css) {
      injectStyle(css);
    }
  }, [css]);

  const isInLoadingState = !initialized || isLoading;

  // still loading, no page data to render yet
  if (isInLoadingState) {
    // @TODO: ENR-2491 theme this
    return <Loader />;
  }

  // handle page container (ie navigation functions)
  return <Page />;
};

/**
 * Injects custom styles in the document head
 */
function injectStyle(css: string) {
  const head = document.head || document.getElementsByTagName('head')[0];
  const style = document.createElement('style');
  style.type = 'text/css';
  style.appendChild(document.createTextNode(css));
  head.appendChild(style);
}

export default React.memo(FlowContainer);
